import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  HostBinding,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { injectCheckImpersonation } from '@rd/core/services/impersonation/impersonation.service';
import { UrlUtilsService } from '@rd/core/services/url-utils/url-utils.service';
import { TermsLinkHelper } from '@rd/helpers/terms-link.helper';
import { RedeemCashSettingService } from '../../services/redeem-cash-setting/redeem-cash-setting.service';

@Component({
  selector: 'rd-rcv2-review-form',
  templateUrl: './review-form.component.html',
  styleUrls: ['./review-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class RcV2ReviewFormComponent implements OnInit {
  @HostBinding('class.rd-rcv2-review-form') componentClass = true;
  @Input() cashRedemptionLoading: boolean;
  @Input() promocodeLoading = false;
  @Output() submitEvent = new EventEmitter<void>();

  reviewFormGroup: UntypedFormGroup;
  isFormInvalidOnSubmit: boolean;
  privacyPolicyUrl: URL;
  tncsUrl: URL;

  constructor(
    private termsLinkHelper: TermsLinkHelper,
    private urlUtilsService: UrlUtilsService,
    private translateService: TranslateService,
    private fb: UntypedFormBuilder,
    private redeemCashSettingService: RedeemCashSettingService,
  ) {}

  ngOnInit(): void {
    // @ts-ignore
    this.tncsUrl = this.shouldDisplayTncCheckboxes
      ? this.urlUtilsService.tncsUrl()
      : null;
    // @ts-ignore
    this.privacyPolicyUrl = this.shouldDisplayTncCheckboxes
      ? this.urlUtilsService.privacyPolicyUrl()
      : null;
    this.reviewFormGroup = this.fb.group({
      acceptTnc: [
        false,
        this.shouldDisplayTncCheckboxes ? Validators.requiredTrue : null,
      ],
    });
  }

  termsTranslationParams(): {
    termsAndConditions: string;
    privacyPolicy: string;
  } {
    const termsAndConditionText = this.translateService.instant(
      'redeem_cash_v2.review.terms_and_conditions',
    );
    const privacyPolicyText = this.translateService.instant(
      'gift_card.review.accept_tnc.privacy_policy',
    );

    return {
      termsAndConditions: this.termsLinkHelper.createTermsLink({
        termsLinkText: termsAndConditionText,
        termsLinkUrl: this.tncsUrl,
      }),
      privacyPolicy: this.termsLinkHelper.createTermsLink({
        termsLinkText: privacyPolicyText,
        termsLinkUrl: this.privacyPolicyUrl,
      }),
    };
  }

  emitSubmitEventWithImpersonateGuard = injectCheckImpersonation(() => {
    this.submitEvent.emit();
  });

  emitSubmitEvent(): void {
    this.isFormInvalidOnSubmit = this.reviewFormGroup.invalid;
    if (!this.isFormInvalidOnSubmit) {
      this.emitSubmitEventWithImpersonateGuard();
    }
  }

  invalidFormClass(): string | undefined {
    if (this.isFormInvalidOnSubmit) {
      return 'invalid-form';
    }
  }

  get shouldDisplayTncCheckboxes(): boolean {
    return this.redeemCashSettingService.shouldRequireAgreeTnc;
  }

  showErrorMessage(): boolean {
    return this.isFormInvalidOnSubmit && this.reviewFormGroup.invalid;
  }
}
